import useOrderForTree from "./useOrderForTree";
import JTree from "../JTree/JTree";
import { TreeProduct } from "./types";

import { Order } from "../../types";

type Props = {
  allowOrderWithoutChildrenCheck?: boolean;
  checkedOrders?: Order[];
  displayOrderWithoutProducts?: boolean;
  expandAllNodes?: boolean;
  hideProducts?: boolean;
  hideProductsWithQtyZero?: boolean;
  orders: Order[];
  onCheck?: (checkedProducts: Order[]) => void;
  showCheckBox?: boolean;
  onProductQuantityChange?: (quantity: number, product: any) => void;
  onProductDelete?: (product: TreeProduct) => void;
  showExpandAll?: boolean;
};

function JOrderTree({
  allowOrderWithoutChildrenCheck,
  checkedOrders,
  displayOrderWithoutProducts = false,
  expandAllNodes,
  hideProducts = false,
  hideProductsWithQtyZero = false,
  onCheck = () => {},
  orders,
  onProductQuantityChange,
  onProductDelete,
  showCheckBox = false,
  showExpandAll = true,
}: Props) {
  const { formatOrderForTree } = useOrderForTree({
    allowOrderWithoutChildrenCheck,
    displayOrderWithoutProducts,
    hideProducts,
    hideProductsWithQtyZero,
    onProductDelete,
    onProductQuantityChange,
    showCheckbox: showCheckBox,
  });

  const orderNodes = orders
    .map((o) => formatOrderForTree(o))
    .filter((node) => node !== null);

  const checkedOrderNodes = checkedOrders
    ? checkedOrders
        .map((o) => formatOrderForTree(o))
        .flatMap(
          (o) =>
            o &&
            o.children?.flatMap((sub) => {
              if (sub && sub.children) {
                return sub.children.flatMap((product) => product.value);
              }
              return sub.value;
            })
        )
    : undefined;

  const handleOnCheck = (checked: string[]) => {
    const newOrders = checked
      .map((c) => JSON.parse(c))
      .flatMap((x) => {
        const { orderId, productId, suborderId } = x;

        const findOrderProducts = (
          orders: Order[],
          orderId,
          productId,
          suborderId
        ): Order => {
          const order = orders.find((order) => order.id === orderId);
          if (!order) return undefined;
          return {
            ...order,
            suborders: order.suborders
              .filter((suborder) => suborder.id === suborderId)
              .map((suborder) => ({
                ...suborder,
                suborderProducts: suborder.suborderProducts.filter(
                  (subProduct) => subProduct.id === productId
                ),
              })),
          } as Order;
        };

        if (productId && suborderId && orderId) {
          return findOrderProducts(orders, orderId, productId, suborderId);
        }
        if (suborderId && orderId) {
          const order = orders.find((order) => order.id === orderId);
          if (!order) return undefined;
          return {
            ...order,
            suborders: order.suborders.filter(
              (suborder) => suborder.id === suborderId
            ),
          };
        }
        if (orderId) {
          return orders.find((order) => order.id === orderId);
        }
        return undefined;
      });
    onCheck(newOrders);
  };

  return (
    <JTree
      expandAllNodes={expandAllNodes}
      nodes={orderNodes}
      forceCheckedNodes={checkedOrderNodes}
      onCheck={handleOnCheck}
      showExpandAll={showExpandAll}
    />
  );
}
export default JOrderTree;
